import { Building2, CircleAlert, Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useActiveContext } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * The signed-in landing view. It reflects the shared active-context state (ActiveContextProvider)
 * rather than fetching the eligible-organizations endpoint itself, so an authenticated-but-not-yet-
 * provisioned caller (no `users` row → 401) sees a calm "not associated with an organization yet"
 * message and can still reach the domain-open modules from the nav — never a red error. A genuine
 * failure still shows an error with a retry.
 *
 * UI refinement: the heading is a greeting (the header already carries the "Console" product name),
 * and each context state renders in a card with an icon and clear hierarchy rather than a bare line.
 */
export function DashboardPage() {
  const context = useActiveContext();
  const { t } = useTranslation(['shell', 'common']);

  return (
    <section className="mx-auto w-full max-w-[1320px] space-y-8 p-[clamp(16px,2.4vw,28px)]">
      <header className="space-y-1.5">
        <h2 className="screen-title-display">{t('shell:dashboard.title')}</h2>
        <p className="screen-summary">{t('shell:dashboard.subtitle')}</p>
      </header>

      {context.status === 'loading' && (
        <div className="space-y-2.5" aria-busy="true" aria-live="polite">
          <div className="bg-muted/60 h-16 animate-pulse rounded-lg" />
          <div className="bg-muted/40 h-16 animate-pulse rounded-lg" />
        </div>
      )}

      {context.status === 'unprovisioned' && (
        <Card>
          <CardContent className="flex items-start gap-4 p-6">
            <Inbox className="text-muted-foreground mt-0.5 size-6 shrink-0" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-medium">{t('shell:dashboard.unprovisionedLine1')}</p>
              <p className="text-muted-foreground text-sm">
                {t('shell:dashboard.unprovisionedLine2')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {context.status === 'error' && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 p-6">
            <CircleAlert className="text-destructive size-6 shrink-0" aria-hidden="true" />
            <p className="text-sm">{t('shell:dashboard.error')}</p>
            <Button variant="outline" size="sm" className="ml-auto" onClick={context.reload}>
              {t('common:retry')}
            </Button>
          </CardContent>
        </Card>
      )}

      {context.status === 'ready' && context.eligible.length === 0 && (
        <Card>
          <CardContent className="flex items-start gap-4 p-6">
            <Inbox className="text-muted-foreground mt-0.5 size-6 shrink-0" aria-hidden="true" />
            <p className="text-muted-foreground text-sm">{t('shell:dashboard.emptyOrg')}</p>
          </CardContent>
        </Card>
      )}

      {context.status === 'ready' && context.eligible.length > 0 && (
        <div className="space-y-3">
          <p className="card-label">{t('shell:dashboard.yourOrganizations')}</p>
          <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {context.eligible.map((organization) => (
              <li key={organization.id}>
                <div
                  className="border-border bg-card hover:border-primary/40 flex items-center gap-3
                    rounded-lg border p-4 transition-colors"
                >
                  <span
                    className="bg-secondary text-brand flex size-9 shrink-0 items-center
                      justify-center rounded-md"
                  >
                    <Building2 className="size-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{organization.name ?? organization.code}</p>
                    <p className="text-muted-foreground truncate text-xs">{organization.code}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
